import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DifferenceProps } from '../DifferenceElementView';
import DifferenceDescription from '../DifferenceDescription/DifferenceDescription';
import { rgbToColor } from 'utils/colorUtils';

const useStyles = makeStyles()((theme) => ({
  colorItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  colorSquare: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    border: '1px solid rgba(255, 255, 255, 0.20)',
    flexShrink: 0,
  },
  pointText: {
    fontSize: '12px',
    fontWeight: 600,
  },
}));

// Format color description for display
const formatColorDescription = (description: string): string => {
  return description
    .split('\n')
    .map((line) => {
      // Handle RGB format with source | target
      if (line.startsWith('RGB:')) {
        const parts = line.split('|').map((part) => part.trim());
        const formatted = parts.map((part) => {
          const rMatch = part.match(/R:\s*(-?\d+\.?\d*)/);
          const gMatch = part.match(/G:\s*(-?\d+\.?\d*)/);
          const bMatch = part.match(/B:\s*(-?\d+\.?\d*)/);
          if (rMatch && gMatch && bMatch) {
            return `RGB(${rMatch[1]},${gMatch[1]},${bMatch[1]})`;
          }
          return part;
        });
        return formatted.join(' | ');
      }

      // Handle CMYK format with source | target
      if (line.startsWith('CMYK:')) {
        const parts = line.split('|').map((part) => part.trim());
        const formatted = parts.map((part) => {
          const cMatch = part.match(/C:\s*(-?\d+\.?\d*)/);
          const mMatch = part.match(/M:\s*(-?\d+\.?\d*)/);
          const yMatch = part.match(/Y:\s*(-?\d+\.?\d*)/);
          const kMatch = part.match(/K:\s*(-?\d+\.?\d*)/);
          if (cMatch && mMatch && yMatch && kMatch) {
            return `CMYK(${cMatch[1]},${mMatch[1]},${yMatch[1]},${kMatch[1]})`;
          }
          return part;
        });
        return formatted.join(' | ');
      }

      // Handle LAB format with source | target
      if (line.startsWith('LAB:')) {
        const parts = line.split('|').map((part) => part.trim());
        const formatted = parts.map((part) => {
          const lMatch = part.match(/L:\s*(-?\d+\.?\d*)/);
          const aMatch = part.match(/A:\s*(-?\d+\.?\d*)/);
          const bMatch = part.match(/B:\s*(-?\d+\.?\d*)/);
          if (lMatch && aMatch && bMatch) {
            return `LAB(${lMatch[1]},${aMatch[1]},${bMatch[1]})`;
          }
          return part;
        });
        return formatted.join(' | ');
      }

      // Handle ΔE format - keep as is
      if (line.startsWith('ΔE:')) {
        return line;
      }

      return line;
    })
    .join('\n');
};

const ColorDifference = ({ difference, multipleSelectionMode = false }: DifferenceProps) => {
  const { classes } = useStyles();

  if (!difference.colorDetails) return null;

  const { colorDetails, source, description } = difference;
  const selectedIndex = colorDetails.selectedClosestColorIndex ?? 0;
  const closestColorName = colorDetails.closestColors?.[selectedIndex]?.color.name ?? '';

  const renderSourceColor = (rgbString: string) => (
    <Box className={classes.colorItem}>
      <Box className={classes.colorSquare} style={{ backgroundColor: rgbToColor(rgbString) }} />
    </Box>
  );

  const renderTargetColor = (rgbString: string) => (
    <Box className={classes.colorItem}>
      <Box className={classes.colorSquare} style={{ backgroundColor: rgbToColor(rgbString) }} />
      <Typography className={classes.pointText}>{closestColorName}</Typography>
    </Box>
  );

  const formattedDescription = description ? formatColorDescription(description) : '';

  return (
    <DifferenceDescription
      difference={difference}
      sourceDescription={renderSourceColor(colorDetails.sampledRgb)}
      targetDescription={renderTargetColor(colorDetails.closestRgb)}
      sourcePageNumberText={`selected (P${source.location.pageNumber})`}
      targetPageNumberText="digital approximation"
      multipleSelectionMode={multipleSelectionMode}
      showExtra
      extraDescription={formattedDescription}
    />
  );
};

export default ColorDifference;
